import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import ContentDeliveries from '../../../pages/documents/accounts/ContentDeliveries'
import {
  Cargo,
  removeCargosFromStore,
  setCargosGroup,
  setCurrentCargo,
  setIsOnlyCargo,
  setIsVisibleDeleteManyModalCargo,
  setIsVisibleDeleteModalCargo,
  setIsVisibleModalCargo,
  setSelectedCargosForDeletion,
  setVisibleContentDeliveries,
  setVisibleSideModal,
} from '../../../redux/reducers/cargos'
import {
  fetchAllInvoices,
  fetchAttachCargo,
  setIsVisibleCreateModalInvoice,
} from '../../../redux/reducers/invoices'
import { cargosSelector } from '../../../redux/selectors/cargos-selector'
import { clientsSelector } from '../../../redux/selectors/clients-selector'
import { invoicesSelector } from '../../../redux/selectors/invoices-selector'
import { AppDispatch } from '../../../redux/store'
import { formatDateToDDMMYYYY } from '../../../utils/date'
import { getAmount, getItemCurrency } from '../../../utils/getAmount'
import { getRole } from '../../../utils/getRole'
import InvoiceCreateModal from '../invoice-modal/InvoiceCreateModal'
import UnpaidCargoCard from './UnpaidCargoCard'
import UnpaidCargosActionsPanel from './UnpaidCargosActionsPanel'
import UnpaidCargosHeader from './UnpaidCargosHeader'
import styles from './CargoSideModal.module.css'

const CargoSideModal = React.memo(function CargoSideModal() {
  const dispatch = useDispatch<AppDispatch>()
  const { isClient } = getRole()

  const [isOpenedDropdown, setOpenedDropdown] = useState(false)

  const {
    isVisibleSideModal,
    currentCargoGroup,
    isVisibleContentDeliveries,
    isVisibleDeleteManyModalCargo,
    cargos,
  } = useSelector(cargosSelector)
  const { allInvoices, isPendingGetAllInvoices, isVisibleCreateModalInvoice } =
    useSelector(invoicesSelector)
  const { clients, isVisibleModalClient } = useSelector(clientsSelector)
  const { cargos: cargosData } = useSelector(cargosSelector)

  // Итого к оплате: сумма грузов без счёта (invoice_id === null), по валютам (те же данные, что в панели «К оплате»)
  const totalToPayFromCargos = useMemo(() => {
    const list = Object.values(cargosData || {}).flat()
    const onlyUnpaid = list.filter(
      (c: { invoice_id?: number | null }) => c.invoice_id == null
    )
    const sums: Record<string, number> = {}
    onlyUnpaid.forEach((c: { amount: number; currency?: string }) => {
      const cur = c.currency || 'BYN'
      sums[cur] = (sums[cur] || 0) + Number(c.amount)
    })
    return Object.entries(sums)
      .filter(([, amount]) => amount > 0)
      .map(([currency, amount]) => ({ currency, amount }))
  }, [cargosData])

  const payer = currentCargoGroup?.[0]?.payer === 1
  const isThirdPartyPayer = currentCargoGroup?.[0]?.payer === 2

  const clientId =
    isThirdPartyPayer && currentCargoGroup?.[0]?.third_id
      ? currentCargoGroup[0].third_id
      : payer
        ? currentCargoGroup?.[0]?.to_id
        : currentCargoGroup?.[0]?.from_id

  // Функция для получения имени клиента по ID
  const getClientName = (clientId: number) => {
    const client = clients.find((c) => c.id === clientId)
    return client ? client.entity_name || client.name : 'Неизвестный клиент'
  }

  // Получаем имя для отображения в заголовке
  const getDisplayName = () => {
    if (isThirdPartyPayer && currentCargoGroup?.[0]?.third_id) {
      return getClientName(currentCargoGroup[0].third_id)
    }
    return payer
      ? currentCargoGroup?.[0]?.to?.entity_name
      : currentCargoGroup?.[0]?.from?.entity_name
  }

  // Мемоизируем список счетов для клиента
  const getInvoices = useMemo(
    () => allInvoices.filter((item) => item.client_id === clientId),
    [allInvoices, clientId]
  )

  // Функция для получения актуальных грузов клиента из общего списка
  const getCurrentClientCargos = useCallback(() => {
    if (!clientId || !cargos) return []

    // Если это клиент - получаем все грузы
    if (isClient) {
      return Object.values(cargos).flat()
    }

    // Для обычных пользователей - грузы конкретного клиента
    return cargos[clientId.toString()] || []
  }, [clientId, cargos])

  // Мемоизируем актуальные грузы клиента
  const currentClientCargos = useMemo(
    () => getCurrentClientCargos(),
    [getCurrentClientCargos]
  )

  const [selectedCargos, setSelectedCargos] = useState<number[]>([])
  const [selectAll, setSelectAll] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState('')
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('')

  const handleCargoSelect = (cargoId: number) => {
    setSelectedCargos((prev) =>
      prev.includes(cargoId)
        ? prev.filter((id) => id !== cargoId)
        : [...prev, cargoId]
    )
  }

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCargos([])
    } else {
      setSelectedCargos((currentCargoGroup ?? []).map((cargo) => cargo.id))
    }
    setSelectAll(!selectAll)
  }

  // Мемоизируем вычисления сумм
  const totalSelectedAmount = useMemo(
    () =>
      (currentCargoGroup ?? [])
        .filter((cargo) => selectedCargos.includes(cargo.id))
        .reduce((sum, cargo) => sum + cargo.amount, 0),
    [currentCargoGroup, selectedCargos]
  )

  const commonTotalSelectedAmount = useMemo(
    () =>
      (currentCargoGroup ?? []).reduce((sum, cargo) => sum + cargo.amount, 0),
    [currentCargoGroup]
  )

  const handleSetInvoice = (id: number, date: string) => {
    setSelectedInvoice(`№${id} от ${formatDateToDDMMYYYY(date)}`)
    setSelectedInvoiceId(String(id))
  }

  const handleEditCargo = (item: any) => {
    dispatch(setCurrentCargo(item))
    dispatch(setIsVisibleModalCargo(true))
    dispatch(setIsOnlyCargo(true))
  }

  const handleDeleteCargo = (item: any) => {
    dispatch(setIsOnlyCargo(true))
    dispatch(setIsVisibleDeleteModalCargo(true))
    dispatch(setCurrentCargo(item))
    setSelectedCargos([])
    setSelectedInvoice('')
  }

  const handleCreateInvoice = () => {
    dispatch(setIsVisibleCreateModalInvoice(true))
    dispatch(setVisibleSideModal(false))
    dispatch(setVisibleContentDeliveries(false))
  }

  const handleAttachInvoice = () => {
    dispatch(
      fetchAttachCargo({ invoiceId: selectedInvoiceId, selectedCargos })
    ).then((res) => {
      //@ts-ignore
      if (!res.error) {
        dispatch(removeCargosFromStore({ cargoIds: selectedCargos }))

        if (currentCargoGroup) {
          const updatedCargoGroup = currentCargoGroup.filter(
            (cargo) => !selectedCargos.includes(cargo.id)
          )
          dispatch(setCargosGroup(updatedCargoGroup))
        }

        setSelectedCargos([])
        setSelectedInvoice('')
        setSelectedInvoiceId('')
      }
    })
  }

  // Мемоизируем функцию получения валюты
  const getCurrency = useCallback(() => {
    return getItemCurrency(currentCargoGroup?.[0]?.currency || '')
  }, [currentCargoGroup])
  useEffect(() => {
    setSelectAll(false)
    setSelectedInvoice('')
  }, [currentCargoGroup, isVisibleSideModal])

  useEffect(() => {
    return () => {
      setOpenedDropdown(false)
      setSelectedInvoice('')
    }
  }, [currentCargoGroup])

  // Очищаем выбранные грузы после успешного массового удаления
  useEffect(() => {
    if (!isVisibleDeleteManyModalCargo) {
      setSelectedCargos([])
      setSelectAll(false)
    }
  }, [isVisibleDeleteManyModalCargo])

  const handleDeleteMany = () => {
    if (!selectedCargos.length) {
      return
    }
    dispatch(setSelectedCargosForDeletion(selectedCargos))
    dispatch(setIsVisibleDeleteManyModalCargo(true))
  }

  useEffect(() => {
    if (!currentCargoGroup?.length) {
      dispatch(setVisibleContentDeliveries(false))
      // dispatch(setVisibleSideModal(false))
    }
  }, [currentCargoGroup?.length, dispatch])

  // Обновляем currentCargoGroup при изменении грузов (например, после удаления счета)
  useEffect(() => {
    // Не обновляем если модальное окно не видимо - это может быть из-за операции добавления в счет
    if (!isVisibleSideModal || !isVisibleContentDeliveries) {
      return
    }

    if (currentCargoGroup && currentClientCargos.length > 0) {
      // Сравниваем ID грузов в currentCargoGroup с актуальными грузами
      const currentIds = new Set(currentCargoGroup.map((cargo) => cargo.id))
      const actualIds = new Set(currentClientCargos.map((cargo) => cargo.id))

      // Если есть новые грузы (например, после возврата от счета)
      const newCargos = currentClientCargos.filter(
        (cargo) => !currentIds.has(cargo.id)
      )

      if (newCargos.length > 0) {
        // Обновляем currentCargoGroup, добавляя новые грузы
        const updatedCargoGroup = [...currentCargoGroup, ...newCargos]
        dispatch(setCargosGroup(updatedCargoGroup))
      }

      // Если есть удаленные грузы, удаляем их из currentCargoGroup
      const removedCargos = currentCargoGroup.filter(
        (cargo) => !actualIds.has(cargo.id)
      )
      if (removedCargos.length > 0) {
        const updatedCargoGroup = currentCargoGroup.filter((cargo) =>
          actualIds.has(cargo.id)
        )
        dispatch(setCargosGroup(updatedCargoGroup))
      }
    }
  }, [
    currentClientCargos,
    currentCargoGroup,
    dispatch,
    isVisibleSideModal,
    isVisibleContentDeliveries,
  ])

  // Загружаем счета клиента когда открывается второй слайд (контент клиента)
  useEffect(() => {
    if (isVisibleSideModal && isVisibleContentDeliveries && clientId) {
      dispatch(fetchAllInvoices({ client: clientId.toString() }))
    }
  }, [isVisibleSideModal, isVisibleContentDeliveries, clientId, dispatch])

  useEffect(() => {
    return () => {
      dispatch(setIsOnlyCargo(false))
    }
  }, [dispatch])

  return (
    <>
      <div
        className={classNames(
          styles.slidePanel,
          styles.slideLeft,
          styles.level1,
          {
            [styles.opened]: isVisibleSideModal, // Показываем когда открыта боковая панель
          }
        )}
      >
        <div className={styles.slidePanelTitle}>
          <div className="h4 mt-0 mb-0">К оплате</div>
          <div
            onClick={() => {
              dispatch(setCargosGroup([]))
              dispatch(setVisibleSideModal(false))
              dispatch(setVisibleContentDeliveries(false))
              setSelectedCargos([])
              setSelectedInvoice('')
            }}
            className={styles.closeSlidePanel}
          >
            ×
          </div>
          {totalToPayFromCargos.length > 0 && (
            <div
              className={classNames(
                styles.slidePanelTotalWrap,
                styles.small2,
                'pt-2'
              )}
            >
              {totalToPayFromCargos.map(({ currency, amount }) => (
                <span
                  key={currency}
                  className={classNames(styles.totalToPayBadge, styles.small3)}
                >
                  <b>
                    {getAmount(amount)}
                    &nbsp;{getItemCurrency(currency)}
                  </b>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className={styles.slidePanelContent}>
          <ContentDeliveries />
        </div>
      </div>

      {/* HERE  */}

      <div
        className={classNames(
          styles.slidePanel,
          styles.slideLeft,
          styles.level2,
          {
            [styles.opened]: isVisibleSideModal && isVisibleContentDeliveries,
          }
        )}
      >
        <div className={styles.slidePanelTitle}>
          <div className={classNames(styles.h4, 'mt-0 mb-0')}>
            {getDisplayName()}
          </div>
          <div
            onClick={() => {
              // Проверяем, является ли устройство мобильным
              const isMobile = window.innerWidth < 768

              if (isMobile) {
                // На мобиле: закрываем только второй слайд, возвращаемся к первому
                dispatch(setVisibleContentDeliveries(false))
              } else {
                // На десктопе: закрываем всю боковую панель
                dispatch(setVisibleSideModal(false))
                dispatch(setVisibleContentDeliveries(false))
              }

              setSelectedCargos([])
              setSelectedInvoice('')
            }}
            className={styles.closeSlidePanel}
          >
            ×
          </div>
        </div>

        <div className={styles.slidePanelContent}>
          <UnpaidCargosHeader
            checkboxId="shipment_all"
            selectAll={selectAll}
            onSelectAll={handleSelectAll}
            totalAmount={commonTotalSelectedAmount}
            currency={getCurrency()}
          />

          {currentCargoGroup?.map((item) => (
            <UnpaidCargoCard
              key={item.id}
              item={item}
              isSelected={selectedCargos.includes(item.id)}
              isClient={isClient}
              checkboxIdPrefix="shipment"
              onToggleSelect={handleCargoSelect}
              onEdit={handleEditCargo}
              onDelete={handleDeleteCargo}
            />
          ))}
        </div>

        {!isClient && (
          <UnpaidCargosActionsPanel
            totalSelectedAmount={totalSelectedAmount}
            currency={getCurrency()}
            selectedCount={selectedCargos.length}
            onCreateInvoice={handleCreateInvoice}
            invoices={getInvoices}
            isPendingGetAllInvoices={isPendingGetAllInvoices}
            selectedInvoiceLabel={selectedInvoice}
            selectedInvoiceId={selectedInvoiceId}
            isOpenedDropdown={isOpenedDropdown}
            setOpenedDropdown={setOpenedDropdown}
            onSelectInvoice={handleSetInvoice}
            onAttach={handleAttachInvoice}
            onDeleteMany={handleDeleteMany}
          />
        )}
        {isVisibleCreateModalInvoice && !isVisibleModalClient && (
          <InvoiceCreateModal
            setSelectedCargos={setSelectedCargos}
            selectedCargos={selectedCargos}
          />
        )}
      </div>
    </>
  )
})

CargoSideModal.displayName = 'CargoSideModal'

export default CargoSideModal
